import { InboundSipTrunkResponse } from 'apis/business/agent/deployPhone/inboundSipTrunk/types';
import { OutboundSipTrunkResponse } from 'apis/business/agent/deployPhone/outBoundSipTrunk/types';

export type OutboundSipTrunkVariant = {
  agentTelephony: 'byoc' | 'asterisk';
  provider: string;
  telephonyProviderId?: number;
  isSipRegistration?: boolean;
};

export const OUTBOUND_SIP_DEFAULTS: OutboundSipTrunkResponse = {
  direction: 'outbound',
  additional_config: {
    address: '',
    numbers: [{ value: '' }],
    transport: 0,
    auth_password: '',
    auth_username: '',
    media_encryption: 0,
    destination_country: 'US',
    number_type: 'phone',
    outbound_proxy: '',
    proxy: '',
    whitelisted_ip: '',
    carrier_name: '',
    kickcall_trunk_name: '',
    override_from: ''
  }
};

function toTelFieldValues(items?: unknown): { value: string }[] {
  if (!Array.isArray(items) || items.length === 0) return [{ value: '' }];
  return items.map((item) => {
    if (typeof item === 'string' || typeof item === 'number') return { value: String(item) };
    if (item && typeof item === 'object' && 'value' in item) {
      const raw = (item as { value?: unknown }).value;
      return { value: raw == null ? '' : String(raw) };
    }
    return { value: '' };
  });
}

export function mapOutboundFormValues(
  outboundFormData: OutboundSipTrunkResponse | undefined
): OutboundSipTrunkResponse | undefined {
  if (!outboundFormData) return undefined;

  return {
    ...outboundFormData,
    additional_config: {
      ...outboundFormData.additional_config,
      number_type: outboundFormData.additional_config?.number_type ?? 'phone',
      numbers: toTelFieldValues(outboundFormData.additional_config?.numbers)
    }
  };
}

function sipRegistrationPhoneNumber(data: OutboundSipTrunkResponse): string {
  const numbers = data.additional_config?.numbers;
  if (!Array.isArray(numbers) || !numbers.length) return '';

  const first = numbers[0] as { value?: string } | string | undefined;
  if (typeof first === 'string') return first.trim();
  return first?.value?.trim() ?? '';
}

export function buildOutboundTrunkPayload(
  data: OutboundSipTrunkResponse,
  variant: OutboundSipTrunkVariant
): Record<string, unknown> {
  const additionalConfig = { ...data.additional_config };
  const connectionType = variant.isSipRegistration ? 'sip_registration' : 'sip_trunking';
  const numberType = additionalConfig.number_type === 'extension' ? 'extension' : 'phone';

  const payload: OutboundSipTrunkResponse = {
    ...data,
    agent_telephony: variant.agentTelephony,
    additional_config: {
      ...additionalConfig,
      type: connectionType,
      number_type: numberType,
      numbers: variant.isSipRegistration
        ? [sipRegistrationPhoneNumber(data)].filter(Boolean)
        : data.additional_config?.numbers
            ?.map((n: { value: string }) => n.value)
            .filter((val: string) => val.trim() !== '')
    }
  };

  return {
    ...payload,
    provider: variant.provider,
    ...(variant.telephonyProviderId && { telephony_provider_id: variant.telephonyProviderId })
  };
}

export function buildInboundTrunkPayloadFromSipRegistration(
  data: OutboundSipTrunkResponse,
  variant: OutboundSipTrunkVariant
): InboundSipTrunkResponse {
  const phoneNumber = sipRegistrationPhoneNumber(data);
  const numberType = data.additional_config?.number_type === 'extension' ? 'extension' : 'phone';

  return {
    agent_telephony: variant.agentTelephony,
    provider: variant.provider,
    ...(data.name != null && data.name !== '' ? { name: data.name } : {}),
    ...(variant.telephonyProviderId && { telephony_provider_id: variant.telephonyProviderId }),
    additional_config: {
      auth_username: data.additional_config?.auth_username,
      auth_password: data.additional_config?.auth_password,
      numbers: phoneNumber ? [phoneNumber] : [],
      number_type: numberType,
      type: 'sip_registration'
    }
  };
}
